import pino from 'pino';

// Determine if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

// Get log level from environment variable, default to 'info' in production, 'debug' in development
const getLogLevel = (): pino.Level => {
  const envLevel = process.env.LOG_LEVEL?.toLowerCase();
  const validLevels: pino.Level[] = ['fatal', 'error', 'warn', 'info', 'debug', 'trace'];

  if (envLevel && validLevels.includes(envLevel as pino.Level)) {
    return envLevel as pino.Level;
  }

  // Default: debug in development, info in production
  return process.env.NODE_ENV === 'production' ? 'info' : 'debug';
};

// Configure pino options
const pinoConfig: pino.LoggerOptions = {
  level: getLogLevel(),
  // Use pino-pretty for development (human-readable), structured JSON for production
  ...(process.env.NODE_ENV !== 'production' && !isBrowser
    ? {
        transport: {
          target: 'pino-pretty',
          options: {
            colorize: true,
            translateTime: 'HH:MM:ss',
            ignore: 'pid,hostname',
            singleLine: false,
          },
        },
      }
    : {}),
  // Browser-specific configuration
  ...(isBrowser
    ? {
        browser: {
          asObject: false,
          write: {
            debug: console.debug.bind(console),
            error: console.error.bind(console),
            fatal: console.error.bind(console),
            info: console.info.bind(console),
            trace: console.trace.bind(console),
            warn: console.warn.bind(console),
          },
        },
      }
    : {}),
};

// Create the base pino logger instance
const pinoLogger = pino(pinoConfig);

// Create a console-compatible wrapper that matches console.log/error/warn API
// This allows us to use logger.debug(message, ...args) instead of pino's logger.debug(obj, message)
const logger = {
  debug: (...args: unknown[]) => {
    if (args.length === 0) return;
    const [first, ...rest] = args;
    if (rest.length === 0) {
      pinoLogger.debug(first);
    } else {
      pinoLogger.debug({ args: rest }, String(first));
    }
  },
  info: (...args: unknown[]) => {
    if (args.length === 0) return;
    const [first, ...rest] = args;
    if (rest.length === 0) {
      pinoLogger.info(first);
    } else {
      pinoLogger.info({ args: rest }, String(first));
    }
  },
  warn: (...args: unknown[]) => {
    if (args.length === 0) return;
    const [first, ...rest] = args;
    if (rest.length === 0) {
      pinoLogger.warn(first);
    } else {
      pinoLogger.warn({ args: rest }, String(first));
    }
  },
  error: (...args: unknown[]) => {
    if (args.length === 0) return;
    const [first, ...rest] = args;
    if (rest.length === 0) {
      pinoLogger.error(first);
    } else {
      pinoLogger.error({ args: rest }, String(first));
    }
  },
  fatal: (...args: unknown[]) => {
    if (args.length === 0) return;
    const [first, ...rest] = args;
    if (rest.length === 0) {
      pinoLogger.fatal(first);
    } else {
      pinoLogger.fatal({ args: rest }, String(first));
    }
  },
  trace: (...args: unknown[]) => {
    if (args.length === 0) return;
    const [first, ...rest] = args;
    if (rest.length === 0) {
      pinoLogger.trace(first);
    } else {
      pinoLogger.trace({ args: rest }, String(first));
    }
  },
};

// Export the console-compatible logger
export default logger;
